import {
  Checkpoint,
  Client,
  Command,
  Config,
  ThreadState,
} from '@langchain/langgraph-sdk';

import { useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetGraphStudioPath } from '@/utils/useGetGraphStudioPath';

import { useLangGraphClient } from '../api';
import { STUDIO_METADATA } from '../constants';
import { Updates, getCheckpointId, isCheckpoint } from '../data/misc';
import { useActiveThreadId } from '../hooks/useActiveThreadId';
import {
  StudioRenderMode,
  StudioRenderModeContext,
} from '../hooks/useStudioRenderMode';
import { StateContext } from './state';
import { RunStop, abortRun, canAbort, hasActiveRunId } from './state.utils';

export const runner = async (
  langgraph: Client,
  apiUrl: string,
  {
    abort,
    messagesEnabled,
    beforeBreakpoints: beforeBreakpoints,
    afterBreakpoints: afterBreakpoints,
    config,
    assistantId,
    threadId,
    checkpoint,
    input,
    command,
    onRunId,
    runIdToJoin,
    onStop,
    onUpdate,
    onMessageProgress,
  }: {
    abort: AbortController;
    messagesEnabled: boolean;
    beforeBreakpoints: string[];
    afterBreakpoints: string[];
    config: Config;
    threadId: string;
    checkpoint: Checkpoint | string | undefined;
    assistantId: string;
    input: unknown;
    command: Command | undefined;
    runIdToJoin: string | undefined;
    onRunId: (runId: string) => void;
    onStop: (stop: RunStop) => void;
    onUpdate: (update: Updates) => void;
    onMessageProgress: (msgs: Record<string, unknown[]>) => void;
  }
) => {
  let runId: string | undefined;

  try {
    const stream = runIdToJoin
      ? langgraph.runs.joinStream(threadId, runIdToJoin, abort.signal)
      : langgraph.runs.stream(threadId, assistantId, {
          input: input as Record<string, unknown>,
          command,
          metadata: STUDIO_METADATA(apiUrl),
          config: {
            ...config,
            configurable: {
              ...config.configurable,
              thread_id: threadId,
            },
          },
          ...(typeof checkpoint === 'string'
            ? { checkpointId: checkpoint }
            : { checkpoint }),
          signal: abort.signal,
          interruptBefore: beforeBreakpoints,
          interruptAfter: afterBreakpoints,
          streamSubgraphs: true,
          streamMode: messagesEnabled ? ['debug', 'messages'] : ['debug'],
          multitaskStrategy: 'rollback',
          checkpointDuring: true,
        });

    // NOTE: Mutable state
    const runId2NodeName = new Map<string, string>();
    const nodeName2RunId2Msgs = new Map<string, Map<string, unknown>>();
    const lastCheckpointPayload: Record<string, ThreadState['checkpoint']> = {};

    if (runIdToJoin) {
      runId = runIdToJoin;
      onRunId(runId);
    }

    if (typeof checkpoint === 'string') {
      lastCheckpointPayload[''] = {
        thread_id: threadId,
        checkpoint_id: checkpoint,
        checkpoint_ns: '',
        checkpoint_map: {},
      };
    } else if (checkpoint && !checkpoint?.checkpoint_ns) {
      lastCheckpointPayload[''] = {
        thread_id: threadId,
        checkpoint_id: null,
        checkpoint_ns: '',
        checkpoint_map: {},
      };
    }

    const reportMessageProgress = () => {
      // Mutable to immutable mapping
      onMessageProgress(
        Object.fromEntries(
          [...nodeName2RunId2Msgs.entries()].map(([nodeName, msgs]) => [
            nodeName,
            Array.from(msgs.values()),
          ])
        )
      );
    };

    for await (const { event: rawEvent, data } of stream) {
      const [event, ...ns] = rawEvent.split('|');
      if (event === 'messages/metadata') {
        Object.entries(
          data as Record<string, { metadata?: { langgraph_node?: string } }>
        ).forEach(([runId, v]) => {
          const nodeName = v.metadata?.langgraph_node;
          if (nodeName != null) runId2NodeName.set(runId, nodeName);
        });
      }

      if (event === 'messages/complete') {
        nodeName2RunId2Msgs.clear();
      }

      if (event === 'messages/partial' || event === 'messages') {
        data.forEach((msg: any) => {
          const runId = msg.id;
          const nodeName = runId2NodeName.get(runId);
          if (nodeName) {
            const l1 = nodeName2RunId2Msgs.get(nodeName);
            if (l1) l1.set(runId, msg);
            else nodeName2RunId2Msgs.set(nodeName, new Map([[runId, msg]]));
          }
          reportMessageProgress();
        });
      }

      if (event === 'error') {
        onStop({ type: 'error', error: data });
        return;
      }

      if (event === 'metadata') {
        if (data['run_id']) runId = data['run_id'];
        if (runId) onRunId(runId);
      }

      if (event === 'debug') {
        nodeName2RunId2Msgs.clear();
        reportMessageProgress();

        const updateData: Updates = data;
        if (isCheckpoint(updateData)) {
          const ns =
            updateData.payload.checkpoint?.checkpoint_ns ||
            updateData.namespace ||
            '';

          updateData.payload.parent_checkpoint ??=
            lastCheckpointPayload[ns] ?? null;
          lastCheckpointPayload[ns] = updateData.payload.checkpoint ?? null;

          updateData.payload.created_at ??= new Date().toISOString();
          updateData.payload.next ??= [];
        }

        onUpdate({ ...updateData, namespace: ns.join('|') ?? '' });
      }
    }
  } catch (e) {
    if (abort.signal.aborted && runId !== undefined) {
      onStop({ type: 'halted', runId });
    } else {
      onStop({ type: 'error', error: e });
    }
    return;
  }

  if (runId === undefined) {
    onStop({ type: 'error', error: 'No run id' });
  } else {
    onStop({ type: 'completed', runId });
  }
};

export const useGraphThreadSubmit = (options: { messagesEnabled: boolean }) => {
  const [langgraph, apiUrl] = useLangGraphClient();
  const { setRenderMode } = useContext(StudioRenderModeContext);

  const { generatePath } = useGetGraphStudioPath();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hostProjectId = searchParams.get('hostProjectId') ?? undefined;

  const { threadId: _threadId } = useActiveThreadId();

  const run = async (payload: {
    assistantId: string | undefined;
    checkpoint?: Checkpoint | string;
    command?: Command;
    runIdToJoin?: string;
    input?: unknown;
    config?: Config;
    state: StateContext;
  }) => {
    const { input, state, assistantId } = payload;
    if (!assistantId) throw new Error('assistantId is required');
    const abort = new AbortController();

    // abort previous requests
    if (canAbort(state.runState)) abortRun(state.runState);

    // clear updates if they are still present
    state.clearUpdates();
    setRenderMode(StudioRenderMode.INTERACT);

    // check if we have thread id and if not, create a new thread
    let threadId = _threadId;
    if (!threadId) {
      const thread = await langgraph.threads.create();
      threadId = thread.thread_id;

      navigate(generatePath({ hostProjectId, threadId }));
    }

    const hasRunId = hasActiveRunId(state.runState);

    if (!hasRunId) {
      state.setRunState({ type: 'starting', abort });
    }

    const config = payload.config ?? {};

    if (config.configurable?.checkpoint_id)
      delete config.configurable.checkpoint_id;
    if (config.configurable?.thread_id) delete config.configurable.thread_id;

    const checkpoint = payload.checkpoint ?? getCheckpointId(state.threadHead);

    runner(langgraph, apiUrl, {
      abort,
      messagesEnabled: options.messagesEnabled,
      beforeBreakpoints: state.beforeBreakpoints,
      afterBreakpoints: state.afterBreakpoints,
      assistantId: assistantId,
      threadId,
      checkpoint,
      command: payload.command,
      runIdToJoin: payload.runIdToJoin,
      input,
      config,
      onUpdate: (update) => {
        // Only process updates that belong to the current thread context and that have a checkpoint id (if of type checkpoint)
        const currentActiveThreadId = new URLSearchParams(
          window.location.search
        ).get('threadId');
        if (
          currentActiveThreadId !== threadId ||
          (update.type === 'checkpoint' && !getCheckpointId(update.payload))
        )
          return;
        state.addUpdate(update);
      },
      onRunId: (runId) => state.setRunState({ type: 'running', abort, runId }),
      onMessageProgress: (msgs) => {
        // Only process message updates that belong to the current thread context
        const currentActiveThreadId = new URLSearchParams(
          window.location.search
        ).get('threadId');
        if (currentActiveThreadId === threadId) {
          state.setPendingMessages(msgs);
        }
      },
      onStop: (s) => {
        switch (s.type) {
          case 'completed':
            state.setRunState({
              type: 'completed',
            });
            break;
          case 'error': {
            state.setRunState({
              type: 'error',
              error: s.error,
            });
            break;
          }
          case 'halted': {
            // ensure the run is cancelled when the run is halted
            langgraph.runs.cancel(threadId, s.runId);
            state.setRunState({
              type: 'halted',
            });
            break;
          }
        }
      },
    });
  };

  return run;
};
